import { FC, useCallback, useEffect, useRef, useState } from 'react';
import type { CheckboxesSubmissionData, OptionInfo } from '@entities/leadCollector';
import type { CheckboxGroup } from '../lib/getCheckboxesFromBodyForm';
import { Checkbox } from '@shared/ui/kit';
import { OptionGroup } from '../OptionGroup';
import { OptionItemRow } from '../OptionItemRow';

const CHECKBOX_REQUIRED_MESSAGE = 'Должно быть выбрано хотя бы одно значение';

export interface CheckboxesFromFormBodyProps {
  groups: CheckboxGroup[];
  onChange?: (checkboxes: CheckboxesSubmissionData[]) => void;
  registerValidate?: (validate: () => Promise<boolean>) => void;
}

export const CheckboxesFromFormBody: FC<CheckboxesFromFormBodyProps> = ({ groups, onChange, registerValidate }) => {
  const [selectedByGroupId, setSelectedByGroupId] = useState<Record<string, Record<string, string>>>({});
  const [errorsByGroupId, setErrorsByGroupId] = useState<Record<string, string>>({});
  const selectedByGroupIdRef = useRef(selectedByGroupId);
  selectedByGroupIdRef.current = selectedByGroupId;

  const onToggle = useCallback((groupInstanceId: string, option: { instanceId: string; text: string }) => {
    setSelectedByGroupId((prev) => {
      const currentGroupSelection = prev[groupInstanceId] ?? {};
      const nextGroupSelection = { ...currentGroupSelection };

      if (nextGroupSelection[option.instanceId]) {
        delete nextGroupSelection[option.instanceId];
      } else {
        nextGroupSelection[option.instanceId] = option.text;
      }

      return { ...prev, [groupInstanceId]: nextGroupSelection };
    });

    setErrorsByGroupId((prev) => {
      const next = { ...prev };
      delete next[groupInstanceId];
      return next;
    });
  }, []);

  const runValidation = useCallback(async (): Promise<boolean> => {
    if (!groups?.length) return true;

    const nextErrors: Record<string, string> = {};
    let valid = true;

    for (const group of groups) {
      const selected = selectedByGroupIdRef.current[group.instanceId];
      if (!Object.keys(selected ?? {}).length) {
        nextErrors[group.instanceId] = CHECKBOX_REQUIRED_MESSAGE;
        valid = false;
      }
    }

    setErrorsByGroupId(nextErrors);
    return valid;
  }, [groups]);

  useEffect(() => {
    registerValidate?.(runValidation);
    return () => registerValidate?.(undefined as unknown as () => Promise<boolean>);
  }, [registerValidate, runValidation]);

  useEffect(() => {
    if (!groups?.length) {
      onChange?.([]);
      return;
    }

    const result: CheckboxesSubmissionData[] = [];
    for (const group of groups) {
      const selected = selectedByGroupId[group.instanceId];
      const selectedValues = selected ? Object.values(selected) : [];
      if (!selectedValues.length) continue;

      const titleCheckboxes = group.isTitleVisible && group.title ? group.title : undefined;
      result.push({
        titleCheckboxes,
        valueCheckboxes: selectedValues,
      });
    }

    onChange?.(result);
  }, [groups, selectedByGroupId, onChange]);

  if (!groups?.length) return null;

  return (
    <>
      {groups.map((group) => {
        const options = group.options ?? [];
        const selected = selectedByGroupId[group.instanceId] ?? {};
        const groupError = errorsByGroupId[group.instanceId];

        return (
          <OptionGroup key={group.instanceId} group={group} error={groupError}>
            {options.map((option: OptionInfo) => (
              <OptionItemRow key={option.instanceId} option={option}>
                <Checkbox
                  isChecked={!!selected[option.instanceId]}
                  onChange={() => onToggle(group.instanceId, { instanceId: option.instanceId, text: option.text })}
                />
              </OptionItemRow>
            ))}
          </OptionGroup>
        );
      })}
    </>
  );
};
